import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Input, InputGroup, InputGroupText, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { Role, RolesList } from "../../../backoffice/dtos/authentication/role";
import IdItemSelection from "../../views/common/IdItemSelection";

export default function HierarchyRoleEditModal(
    {
        isOpen,
        roleToEdit,
        roles,
        close,
        submit,
        roleType
    } : {
        isOpen: boolean;
        roleToEdit: Role | undefined;
        roles: RolesList;
        close: () => void;
        submit: (role: Role, roleType: 'roles' | 'hierarchyRoles') => void;
        roleType: 'roles' | 'hierarchyRoles';
    }
) {
    const {t} = useTranslation(['user_info','common'])

    const [role, setRole] = useState<Role>(
        roleToEdit ?? { level: 0, name: "", informViaEmail: false, isPrimary: false }
    );

    const title = useMemo(() => {
        if (roleType === 'roles') {
            return "Ρόλοι";
        } else {
            return "Ρόλος ιεραρχίας";
        }
    }, [roleType])

    const typedRoles = useMemo(() => {
        if (roleType === 'roles') {
            return roles.Roles
        } else {
            return roles.HierarchyRoles
        }
    }, [roles, roleType])

    const submitAndClose = useCallback(() => {
        submit(role, roleType)
        close()
    }, [role, roleType])

    const selectRole = useCallback((selectedRoleLevel: number | null) => {
        const selectedRole = typedRoles.find(m => m.level === selectedRoleLevel)

        if (selectedRole === undefined) {
            setRole(ref => ({...ref, level: 0, name: ""}))
        } else {
            setRole(ref => ({...ref, level: selectedRole.level, name: selectedRole.name}))
        }
    }, [typedRoles]);

    const handleChange = ({
        target: { name, value },
    }: React.ChangeEvent<HTMLInputElement>) => {
        setRole((prev) => ({ ...prev, [name]: value }))
    }

    const handleCheckChange = ({
        target: { name, checked },
    }: React.ChangeEvent<HTMLInputElement>) => {
        
        setRole(
            (prev) => {
                let informScheduleCron = prev.informScheduleCron;
                if (name === 'informViaEmail') {
                    informScheduleCron = '';
                }
                return ({ ...prev, [name]: checked, informScheduleCron });
            }
        )
    }

    const roleOptions = useMemo(() => {
        return typedRoles.map((it) => ({ value: it.level, label: it.name }))
    }, [typedRoles])

    return (
        <Modal toggle={close} isOpen={isOpen}>
            <ModalHeader toggle={close} title={title}>
                {
                    roleType === 'roles' ? (
                        t("Role", { ns: 'common' })
                    ) : (
                        t("Hierarchy Role", { ns: 'common' })
                    )
                }
            </ModalHeader>
            <ModalBody>
                <Label for="role" md={2}>{t('Role', { ns: 'common' })}</Label>
                <IdItemSelection id={role.level} options={roleOptions} setSelection={selectRole}/>
                <div style={{height: 20}}/>
                {
                    roleType === "hierarchyRoles" && (
                        <>
                            <Row>
                                <Col >
                                    <InputGroup>
                                        <Input type="switch" name='isPrimary' onChange={handleCheckChange} checked={role.isPrimary === true} className="me-2" />
                                        <Label check>{t('Primary Role', { ns: 'common' })}</Label>
                                    </InputGroup>
                                </Col>
                            </Row>
                            <div style={{height: 10}}/>
                            <Row>
                                <Col >
                                    <InputGroup>
                                        <Input type="switch" name='informViaEmail' onChange={handleCheckChange} checked={role.informViaEmail === true} className="me-2"/>
                                        <Label check>{t('Inform Via Email', { ns: 'common' })}</Label>
                                    </InputGroup>
                                </Col>
                            </Row>

                            {
                                role.informViaEmail === true && (
                                    <>
                                        <div style={{height: 10}}/>
                                        <Label for="informScheduleCron" md={5}>{t('Inform Schedule', { ns: 'common' })} (Cron)</Label>
                                        <InputGroup>
                                            <Input type="text" name='informScheduleCron' onChange={handleChange} value={role.informScheduleCron} placeholder="* * * * *" />
                                        </InputGroup>
                                    </>
                                )
                            }
                        </>
                    )
                }
            </ModalBody>
            <ModalFooter>
                <Button color='primary' onClick={submitAndClose}>
                    <span className='fa fa-lg fa-save me-2' />
                    { roleToEdit !== undefined ? t('Update') : t('Add') }
                </Button>
            </ModalFooter>
        </Modal>
    )
}
